import * as React from 'react';
import { DragElementWrapper, DragSource, DragSourceOptions, DropTarget, DropTargetMonitor } from 'react-dnd';

let draggingIndex = -1;

type AdditionalClassificationDragRecord = {
    isRoot: boolean;
};

type BeginDragProps = {
    index?: number;
    record?: AdditionalClassificationDragRecord;
};

type RowTargetProps = {
    index?: number;
    record?: AdditionalClassificationDragRecord;
    moveRow?: (dragIndex: number, hoverIndex: number) => void;
};

const rowSource = {
    canDrag(props: BeginDragProps) {
        return props.record?.isRoot === true && props.index !== undefined;
    },
    beginDrag(props: BeginDragProps) {
        draggingIndex = props.index!;

        return {
            index: props.index,
            record: props.record
        };
    }
};

const rowTarget = {
    canDrop(props: RowTargetProps, monitor: DropTargetMonitor) {
        const item = monitor.getItem();

        return props.record?.isRoot === true && item?.record?.isRoot === true && props.moveRow !== undefined;
    },
    drop(props: RowTargetProps, monitor: DropTargetMonitor) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;

        if (dragIndex === undefined || hoverIndex === undefined || dragIndex === hoverIndex || !props.moveRow) {
            return;
        }

        props.moveRow(dragIndex, hoverIndex);
        monitor.getItem().index = hoverIndex;
    }
};

type RowProps = {
    isOver: boolean;
    canDrop: boolean;
    connectDragSource: DragElementWrapper<DragSourceOptions>;
    connectDropTarget: DragElementWrapper<unknown>;
    style: React.CSSProperties;
    className?: string;
    index?: number;
    record?: AdditionalClassificationDragRecord;
    moveRow?: (dragIndex: number, hoverIndex: number) => void;
};

const AdditionalClassificationDraggableRowImpl: React.FC<RowProps> = ({
    isOver,
    canDrop,
    connectDragSource,
    connectDropTarget,
    moveRow,
    index,
    record,
    ...restProps
}) => {
    let className = restProps.className || '';

    if (isOver && canDrop && index !== undefined) {
        if (index > draggingIndex) {
            className += ' drop-over-downward';
        } else if (index < draggingIndex) {
            className += ' drop-over-upward';
        }
    }

    return connectDragSource(connectDropTarget(<tr {...restProps} className={className} />));
};

export const AdditionalClassificationDraggableRow = DropTarget(
    'additional-classification-row',
    rowTarget,
    (connect, monitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop()
    })
)(
    DragSource('additional-classification-row', rowSource, connect => ({
        connectDragSource: connect.dragSource()
    }))(AdditionalClassificationDraggableRowImpl)
);
